import recertificationIcon from 'assets/svgs/icon_ls_recertification.svg'
import expiredIcon from 'assets/svgs/icon_ls_expired.svg'
import { FormattedMessage } from 'react-intl'
import ModulesList from 'components/EVLevel/ModulesList'
import { Box, CircularProgress, Paper, Typography, Stack, Button } from '@mui/material'
import { IEVTrainingLevel, ITrainingLevelDetails } from 'interfaces'
import { formatDate } from 'utils/formatDate'
import { useReSelectRecertificationModuleMutation } from 'store/api'
import { LightTooltip } from 'routes/Dashboard/style'

type Props = {
  selectedEVTrainingLevel: IEVTrainingLevel
  selectedEvLevelUUID: string
  evLevelData: ITrainingLevelDetails
  onClosePortalModal: () => void
  isManagerView: boolean
  passingScore: number
  showRecertificationExtraMessage: boolean
  recertificationMaxAttempts: number
  showRecertificationMaxAttemptsMessage: boolean
  userUUID: string
}

export default function Recertification({
  selectedEVTrainingLevel,
  selectedEvLevelUUID,
  evLevelData,
  onClosePortalModal,
  isManagerView,
  showRecertificationExtraMessage,
  recertificationMaxAttempts,
  showRecertificationMaxAttemptsMessage,
  passingScore,
  userUUID,
}: Props) {
  const { isExpired, expiryDate, name } = selectedEVTrainingLevel

  const [reSelectRecertificationModule, { isLoading: isReSelecting }] =
    useReSelectRecertificationModuleMutation()

  const showReSelectError =
    evLevelData.allowRecertificationModeSelection &&
    !evLevelData.canSelectRecertificationMode &&
    !evLevelData.canReSelectRecertificationMode

  const showReSelectButton =
    evLevelData.canReSelectRecertificationMode || showReSelectError

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: isExpired ? '#9E1B32' : '#5B788F',
        p: 1,
        pt: 2,
      }}
    >
      <Stack direction="row" mb={2} color="white">
        <Box pl={1.5} pr={3} flexShrink={0}>
          <Box display="flex" justifyContent="center" mb={1}>
            <img
              src={isExpired ? expiredIcon : recertificationIcon}
              width={40}
              height={40}
              alt="recertification"
            />
          </Box>

          <Typography align="center">
            {expiryDate && !isExpired ? <FormattedMessage id="evLevel.expires" /> : null}
            {isExpired ? <FormattedMessage id="evLevel.expired" /> : null}
          </Typography>
          {expiryDate && (
            <Typography align="center">{formatDate(expiryDate as string)}</Typography>
          )}
        </Box>

        <Box>
          <Typography style={{ fontWeight: 'bold' }}>
            <FormattedMessage
              id={
                isExpired
                  ? 'evLevel.certificationHasExpired'
                  : 'evLevel.certificationIsDueToExpire'
              }
              values={{ name }}
            />
          </Typography>
          {showRecertificationMaxAttemptsMessage && (
            <Box style={{ marginTop: '10px' }}>
              <p style={{ margin: '0px', fontWeight: 'bold' }}>
                <FormattedMessage
                  id={'evLevel.certificationAttempts'}
                  values={{ number_attempts: recertificationMaxAttempts }}
                />
              </p>
              <p style={{ margin: '0px' }}>
                <FormattedMessage
                  id={'evLevel.certificationFail'}
                  values={{ passing_score: passingScore }}
                />
              </p>
            </Box>
          )}

          <Box style={{ paddingRight: '5%', paddingBottom: '10px' }}>
            {showRecertificationExtraMessage && (
              <>
                <br />
                <Typography style={{ paddingBottom: '5px' }}>
                  <FormattedMessage id={'evLevel.certificationIsDueToExpireTitle1'} />
                </Typography>
                <Typography style={{ paddingBottom: '5px' }}>
                  <FormattedMessage id={'evLevel.certificationIsDueToExpireTitle2'} />
                </Typography>
                <Typography>
                  <FormattedMessage id={'evLevel.certificationIsDueToExpireTitle3'} />
                </Typography>
              </>
            )}
          </Box>
          {evLevelData.recertificationModules &&
            evLevelData.recertificationModules?.length > 1 && (
              <Typography my={2}>
                <FormattedMessage id={'evLevel.recertificationPathways'} />
              </Typography>
            )}
        </Box>
        {showReSelectButton && (
          <Box display="flex" alignItems="center" flexShrink={0} pl={1.5} pr={2.5}>
            <LightTooltip
              title={
                showReSelectError ? (
                  <Box>
                    <Typography fontSize={14}>
                      To reselect your optional choices, you must first be withdrawn from
                      your current classroom session.
                    </Typography>
                    <Typography fontSize={14} mt={1}>
                      Please contact your manager or market administrator for assistance.
                    </Typography>
                  </Box>
                ) : (
                  ''
                )
              }
              arrow
              enterTouchDelay={0}
              leaveTouchDelay={3000}
            >
              <span>
                <Button
                  variant="outlined"
                  size="small"
                  color="secondary"
                  disabled={isReSelecting || showReSelectError}
                  onClick={() => reSelectRecertificationModule({ userUUID })}
                  sx={{ whiteSpace: 'nowrap', minWidth: 100 }}
                >
                  {isReSelecting ? <CircularProgress size={18} /> : 'Re Select'}
                </Button>
              </span>
            </LightTooltip>
          </Box>
        )}
      </Stack>
      {evLevelData.recertificationModules ? (
        <ModulesList
          evLevelUUID={selectedEvLevelUUID}
          modules={evLevelData.recertificationModules}
          onClosePortalModal={onClosePortalModal}
          isManagerView={isManagerView}
          showSelectButton={evLevelData.canSelectRecertificationMode}
          userUUID={userUUID}
        />
      ) : null}
    </Paper>
  )
}
